import React from 'react';
import { PlusCircle, MessageSquare, Trash2, ChevronLeft, History, DollarSign, GraduationCap } from 'lucide-react';

interface Chat {
  title: string;
  messages: any[];
  mode?: 'finance' | 'education' | 'chat';
}

interface SidebarProps {
  isOpen: boolean;
  chats: Chat[];
  currentChatIndex: number | null;
  mode?: 'finance' | 'education' | 'chat';
  onNewChat: () => void;
  onLoadChat: (index: number) => void;
  onDeleteChat: (index: number, e: React.MouseEvent) => void;
  onToggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  chats,
  currentChatIndex,
  mode = 'chat',
  onNewChat,
  onLoadChat,
  onDeleteChat,
  onToggleSidebar,
}) => {
  return (
    <div
      className={`fixed top-0 bottom-0 left-0 transform ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } w-[85%] sm:w-64 bg-blue-600 transition-transform duration-300 ease-in-out z-[150] flex flex-col shadow-lg overflow-hidden mt-[max(calc(4rem+env(safe-area-inset-top)),5rem)] pb-[max(env(safe-area-inset-bottom),1rem)]`}
    >
      {/* Header with History Logo and Close Button */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-white/90" />
          <span className="text-white/90 font-medium">
            {mode === 'finance' ? 'Finance History' : 
             mode === 'education' ? 'Learning History' : 
             'Chat History'}
          </span>
        </div>
        <button
          onClick={onToggleSidebar}
          className="p-2 text-white hover:text-white/90 focus:outline-none"
          aria-label="Close sidebar"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      </div>

      {/* New Chat Button */}
      <div className="p-3">
        <button
          onClick={() => {
            onNewChat();
            onToggleSidebar(); // Optionally close sidebar after starting new chat
          }}
          className="w-full flex items-center justify-center gap-2 bg-white text-blue-600 px-4 py-3 rounded-lg hover:bg-blue-50 active:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation select-none"
          aria-label="Start a new chat"
        >
          <PlusCircle className="h-5 w-5" />
          <span>New Chat</span>
        </button>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y scrollbar-hide">
        {chats.length === 0 ? (
          <p className="text-white text-center p-4 opacity-70">
            No chats yet. Start a new one!
          </p>
        ) : (
          chats.map((chat, index) => (
            <div
              key={index}
              className={`w-full text-left px-4 py-3 hover:bg-blue-700 transition-colors flex items-center justify-between gap-2 ${
                currentChatIndex === index ? 'bg-blue-700/80' : ''
              }`}
            >
              <button
                onClick={() => {
                  onLoadChat(index);
                  onToggleSidebar();
                }}
                className="flex-1 flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-xl transition-all"
              >
                {(() => {
                  // Get icon based on chat mode and title
                  if (chat.mode === 'finance' || chat.title.startsWith('💰')) {
                    return <DollarSign className="h-5 w-5 opacity-70" />;
                  } else if (chat.mode === 'education' || chat.title.startsWith('📚')) {
                    return <GraduationCap className="h-5 w-5 opacity-70" />;
                  } else {
                    return <MessageSquare className="h-5 w-5 opacity-70" />;
                  }
                })()}
                <span className="truncate">{chat.title}</span>
              </button>
              <button
                onClick={(e) => onDeleteChat(index, e)}
                className="text-white opacity-70 hover:opacity-100 p-2 touch-manipulation select-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={`Delete chat: ${chat.title}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Sidebar;